import { z } from 'zod';

const zodPartyNameParamSchema = z
  .string()
  .trim()
  .transform((name) => name || undefined);

export const zodParsingCaseStatusSchema = z.enum([
  'OPEN',
  'IN_PROGRESS',
  'NEEDS_ATTENTION',
  'RESOLVED',
  'DISMISSED',
  'DEBATCHED',
  'ARCHIVED',
]);

export const zodParsingCaseFailureCategorySchema = z.enum([
  'NONE',
  'PARSING_ERROR',
  'OCR_VALIDATION_ERROR',
  'OCR_ENGINE_ERROR',
  'PROCESSING_ERROR',
  'SCHEDULING_ERROR',
  'CONFIGURATION_ERROR',
  'POTENTIAL_DUPLICATE_DETECTED',
  'DATA_QUALITY_ERROR',
  'OTHER',
]);

const zodSiteInfoSchema = z.object({
  id: z.uuid(),
  name: z.string().nullish(),
});

const zodCompanyInfoSchema = z.object({
  id: z.uuid(),
  name: z.string().nullish(),
});

const zodUserInfoSchema = z.object({
  email: z.string().nullish(),
  firstName: z.string().nullish(),
  id: z.uuid(),
  lastName: z.string().nullish(),
});

const zodAccountingReferenceInfoSchema = z.object({
  id: z.uuid(),
  name: z.string().nullish(),
});

// Billing-approval marker from the latest linked job meta
// (`meta.approve_for_billing`). Truthy when billing-approval intent is pending
// on an open case or was applied on resolve. Absent when there is no intent, or
// when a case approve without billing items neutralized an inherited marker.
const zodApproveForBillingSchema = z.object({
  approveAll: z.boolean().nullish(),
  approvedByUserId: z.uuid().nullish(),
  items: z
    .array(
      z.object({
        acceptedQuantityFactor: z.number().nullish(),
        // Body path: string keys and the numeric indexes between them.
        path: z.array(z.union([z.string(), z.number().int()])),
      }),
    )
    .nullish(),
});

const zodRelatedCaseSchema = z.object({
  createdOn: z.string(),
  deliveryNoteDate: z.string().nullish(),
  deliveryNoteId: z.string().nullish(),
  failureCategory: zodParsingCaseFailureCategorySchema.catch('OTHER'),
  finalAssetId: z.uuid().nullish(),
  id: z.uuid(),
  isSubsumed: z.boolean().default(false),
  modifiedOn: z.string().nullish(),
  pdfSasUrl: z.string().nullish(),
  primaryIdentifier: z.string().nullish(),
  status: zodParsingCaseStatusSchema,
});

export const zodParsingCaseBaseSchema = z.object({
  accountingReference: zodAccountingReferenceInfoSchema.nullish(),
  accountingReferenceId: z.uuid().nullish(),
  approveForBilling: zodApproveForBillingSchema.nullish(),
  attemptCount: z.number().int().default(0),
  buyer: zodCompanyInfoSchema.nullish(),
  buyerCompanyId: z.uuid().nullish(),
  buyerName: z.string().nullish(),
  createdOn: z.string(),
  creator: zodUserInfoSchema.nullish(),
  deliveryNoteDate: z.string().nullish(),
  deliveryNoteId: z.string().nullish(),
  dismissedBy: z.uuid().nullish(),
  dismissedOn: z.string().nullish(),
  failureCategory: zodParsingCaseFailureCategorySchema.catch('OTHER'),
  failureDetails: z.record(z.string(), z.unknown()).nullish(),
  failureReason: z.string().nullish(),
  finalAssetId: z.uuid().nullish(),
  id: z.uuid(),
  isSubsumed: z.boolean().default(false),
  issuer: zodCompanyInfoSchema.nullish(),
  issuerId: z.string().nullish(),
  jobId: z.uuid().nullish(),
  jobStatus: z.string().nullish(),
  latestTrigger: z.string().nullish(),
  missingParties: z
    .array(
      z.enum([
        'issuer',
        'technical_issuer',
        'seller',
        'supplier',
        'buyer',
        'recipient',
        'trader',
      ]),
    )
    .nullish(),
  modifiedOn: z.string(),
  note: z.string().nullish(),
  ocrApiJobId: z.string().nullish(),
  parserFormats: z.array(z.string()).nullish(),
  pdfSasUrl: z.string().nullish(),
  primaryIdentifier: z.string().nullish(),
  relatedCases: z.array(zodRelatedCaseSchema).default([]),
  resolvedOn: z.string().nullish(),
  seller: zodCompanyInfoSchema.nullish(),
  sellerCompanyId: z.uuid().nullish(),
  sellerName: z.string().nullish(),
  shipFrom: zodCompanyInfoSchema.nullish(),
  shipFromCompanyId: z.uuid().nullish(),
  shipTo: zodCompanyInfoSchema.nullish(),
  shipToCompanyId: z.uuid().nullish(),
  site: zodSiteInfoSchema.nullish(),
  siteId: z.uuid().nullish(),
  status: zodParsingCaseStatusSchema,
  subsumedByCaseId: z.uuid().nullish(),
  technicalIssuerId: z.uuid().nullish(),
});

export const zodParsingCaseListItemSchema = zodParsingCaseBaseSchema;

export const zodPaginatedParsingCasesResponseSchema = z.object({
  data: z.array(zodParsingCaseListItemSchema),
  hasNextPage: z.boolean(),
  paginatedCount: z.number().int(),
  totalCount: z.number().int().nullish(),
});

export const zodListParsingCasesParamsSchema = z.object({
  accountingReferenceId: z.array(z.uuid()).optional(),
  buyerId: z.uuid().optional(),
  buyerName: zodPartyNameParamSchema.optional(),
  createdFrom: z.string().optional(),
  createdTo: z.string().optional(),
  failureCategory: zodParsingCaseFailureCategorySchema.optional(),
  includeSubsumed: z.boolean().optional().prefault(false),
  limit: z.number().int().min(1).max(500).optional().prefault(50),
  offset: z.number().int().nonnegative().optional().prefault(0),
  orderBy: z.string().optional().prefault('createdOn'),
  returnTotalCount: z.boolean().optional().prefault(false),
  searchString: z.string().optional(),
  sellerId: z.uuid().optional(),
  sellerName: zodPartyNameParamSchema.optional(),
  shipFromId: z.uuid().optional(),
  shipToId: z.uuid().optional(),
  siteId: z.array(z.uuid()).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional().prefault('DESC'),
  status: z.array(zodParsingCaseStatusSchema).optional(),
});

export type ParsingCaseStatus = z.infer<typeof zodParsingCaseStatusSchema>;
export type ParsingCaseFailureCategory = z.infer<
  typeof zodParsingCaseFailureCategorySchema
>;
export type ParsingCaseListItem = z.infer<typeof zodParsingCaseListItemSchema>;
export type PaginatedParsingCasesResponse = z.infer<
  typeof zodPaginatedParsingCasesResponseSchema
>;
export type ListParsingCasesParams = z.infer<
  typeof zodListParsingCasesParamsSchema
>;
